import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable, NgZone, OnDestroy, Optional } from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  finalize,
  map,
  of,
  tap,
} from "rxjs";

import { EnvMicroNotifications } from "../config/env.micro.notifications";
import { ENV } from "../config/env.token";
import { NOTIFICATIONS_ENV } from "../config/micros-env.tokens";
import { Env } from "../config/@env";
import {
  AttachmentUploadResponse,
  CreateTemplateRequest,
  Dispatch,
  DispatchHistoryFilters,
  DispatchHistoryPage,
  DispatchPageMetadata,
  DispatchSummary,
  DispatchReadEvent,
  DispatchRecipientPage,
  FeedItem,
  NotificationPage,
  ImageUploadResponse,
  MessageTemplate,
  SendTemplateRequest,
  SendTemplateResponse,
  StateEvent,
  NotificationDeletedEvent,
  UnreadSummary,
  UpdateTemplateRequest,
} from "../../domain/models/notifications/notification.model";
import { NotificationCampaignInteractionEvent as InteractionEvent } from "../../domain/models/notifications/popup.model";
import { SessionCtl } from "./session.ctl.service";

const STREAM_RECONNECT_BASE_DELAY_MS = 1000;
const STREAM_RECONNECT_MAX_DELAY_MS = 30000;
const STREAM_RECONNECT_JITTER_MS = 500;

export type NotificationFailureSource =
  | "notifications"
  | "unread"
  | "templates"
  | "dispatches"
  | "stream";

export interface NotificationServiceFailure {
  source: NotificationFailureSource;
  status: number;
  code: string;
  message: string;
}

@Injectable({ providedIn: "root" })
export class NotificationsService implements OnDestroy {
  private readonly notificationsSubject = new BehaviorSubject<FeedItem[]>([]);
  private readonly totalUnreadCountSubject = new BehaviorSubject<number>(0);
  private readonly newNotificationIndicatorSubject =
    new BehaviorSubject<boolean>(false);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly notificationPageSubject =
    new BehaviorSubject<DispatchPageMetadata>({
      number: 0,
      size: 10,
      totalElements: 0,
      totalPages: 0,
    });
  private readonly failureSubject =
    new BehaviorSubject<NotificationServiceFailure | null>(null);
  private readonly popupRefreshSubject = new Subject<void>();
  private readonly popupInteractionSubject =
    new Subject<InteractionEvent>();
  private readonly dispatchReadSubject = new Subject<DispatchReadEvent>();
  private readonly dispatchCreatedSubject = new Subject<DispatchSummary>();

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly totalUnreadCount$ = this.totalUnreadCountSubject.asObservable();
  readonly hasNewNotifications$ =
    this.newNotificationIndicatorSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly notificationPage$ = this.notificationPageSubject.asObservable();
  readonly failure$ = this.failureSubject.asObservable();
  readonly popupRefresh$ = this.popupRefreshSubject.asObservable();
  readonly popupInteraction$ = this.popupInteractionSubject.asObservable();
  readonly dispatchRead$ = this.dispatchReadSubject.asObservable();
  readonly dispatchCreated$ = this.dispatchCreatedSubject.asObservable();

  private streamAbortController?: AbortController;
  private streamReconnectTimer?: ReturnType<typeof setTimeout>;
  private connectedUserId?: string;
  private feedUserId?: number;
  private feedPageSize = 0;
  private unreadCountUserId?: number;
  private loadingUnreadCount = false;
  private unreadCountLoadSequence = 0;
  private loadSequence = 0;
  private streamReconnectAttempt = 0;
  private readonly activeFailures = new Map<
    NotificationFailureSource,
    NotificationServiceFailure
  >();
  private readonly pendingReadSnapshots = new Map<string, FeedItem>();
  private publishedFailureKey = "";

  constructor(
    private readonly http: HttpClient,
    private readonly sessionCtl: SessionCtl,
    private readonly zone: NgZone,
    @Inject(ENV) private readonly env: Env,
    @Optional()
    @Inject(NOTIFICATIONS_ENV)
    private readonly notificationsEnv?: EnvMicroNotifications,
  ) {}

  ngOnDestroy(): void {
    this.disconnectStream();
  }

  hasFailure(source: NotificationFailureSource): boolean {
    return this.activeFailures.has(source);
  }

  connectForCurrentUser(pageSize = 10): void {
    this.initializeForCurrentUser(pageSize, false);
  }

  refreshForCurrentUser(pageSize = 10): void {
    this.initializeForCurrentUser(pageSize, true);
  }

  private initializeForCurrentUser(
    pageSize: number,
    refreshFeed: boolean,
  ): void {
    const userId = this.currentUserId();
    if (!userId) {
      this.reset();
      return;
    }

    if (this.feedUserId !== userId) {
      this.reset();
      this.feedUserId = userId;
    }

    if (refreshFeed || pageSize > this.feedPageSize) {
      this.feedPageSize = pageSize;
      this.loadForUser(userId, 0, pageSize).subscribe();
    }
    this.loadUnreadCount(userId);
    this.openStream(userId);
  }

  acknowledgeNewNotifications(): void {
    this.newNotificationIndicatorSubject.next(false);
  }

  loadForUser(
    userId: number,
    page = 0,
    size = 10,
    append = false,
  ): Observable<NotificationPage> {
    const requestId = ++this.loadSequence;
    if (!append) this.loadingSubject.next(true);
    const params = new HttpParams().set("page", page).set("size", size);
    return this.http
      .get<NotificationPage>(this.notificationsUrl(), { params })
      .pipe(
        tap((response) => {
          if (!this.isCurrentLoad(requestId, userId)) return;
          this.clearFailure("notifications");
          this.feedPageSize = append
            ? Math.max(this.feedPageSize, (page + 1) * size)
            : size;
          this.notificationPageSubject.next(response.page);
          const items = append
            ? this.mergeNotifications(
                this.notificationsSubject.value,
                response.content,
              )
            : response.content;
          this.setNotifications(items);
        }),
        catchError((error) => {
          const response = this.emptyNotificationPage(page, size);
          if (!this.isCurrentLoad(requestId, userId)) return of(response);
          this.reportFailure("notifications", error);
          if (!append) this.setNotifications([]);
          return of(response);
        }),
        finalize(() => {
          if (!append && requestId === this.loadSequence)
            this.loadingSubject.next(false);
        }),
      );
  }

  loadUnreadForUser(
    userId: number,
    page = 0,
    size = 20,
  ): Observable<NotificationPage> {
    const requestId = ++this.loadSequence;
    if (page === 0) this.loadingSubject.next(true);
    const params = new HttpParams()
      .set("page", page)
      .set("size", size)
      .set("status", "UNREAD");
    return this.http
      .get<NotificationPage>(this.notificationsUrl(), { params })
      .pipe(
        tap(() => {
          if (!this.isCurrentLoad(requestId, userId)) return;
          this.clearFailure("unread");
        }),
        catchError((error) => {
          const response = this.emptyNotificationPage(page, size);
          if (!this.isCurrentLoad(requestId, userId)) return of(response);
          this.reportFailure("unread", error);
          return of(response);
        }),
        finalize(() => {
          if (page === 0 && requestId === this.loadSequence)
            this.loadingSubject.next(false);
        }),
      );
  }

  listTemplates(): Observable<MessageTemplate[]> {
    return this.http
      .get<MessageTemplate[]>(`${this.notificationsUrl()}/templates`)
      .pipe(
        tap(() => this.clearFailure("templates")),
        catchError((error) => {
          this.reportFailure("templates", error);
          return of([]);
        }),
      );
  }

  createTemplate(
    payload: CreateTemplateRequest,
  ): Observable<MessageTemplate | null> {
    return this.http
      .post<MessageTemplate>(`${this.notificationsUrl()}/templates`, payload)
      .pipe(catchError(() => of(null)));
  }

  updateTemplate(
    templateId: string,
    payload: UpdateTemplateRequest,
  ): Observable<MessageTemplate | null> {
    return this.http
      .patch<MessageTemplate>(
        `${this.notificationsUrl()}/templates/${templateId}`,
        payload,
      )
      .pipe(catchError(() => of(null)));
  }

  deleteTemplate(templateId: string): Observable<boolean> {
    return this.http
      .delete<void>(`${this.notificationsUrl()}/templates/${templateId}`)
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      );
  }

  uploadTemplateImage(file: File): Observable<ImageUploadResponse | null> {
    const formData = new FormData();
    formData.append("file", file, file.name);

    return this.http
      .post<ImageUploadResponse>(
        `${this.notificationsUrl()}/templates/upload-image`,
        formData,
      )
      .pipe(catchError(() => of(null)));
  }

  uploadTemplateAttachment(
    file: File,
  ): Observable<AttachmentUploadResponse | null> {
    const formData = new FormData();
    formData.append("file", file, file.name);

    return this.http
      .post<AttachmentUploadResponse>(
        `${this.notificationsUrl()}/templates/upload-attachment`,
        formData,
      )
      .pipe(catchError(() => of(null)));
  }

  sendTemplate(
    payload: SendTemplateRequest,
  ): Observable<SendTemplateResponse | null> {
    return this.http
      .post<SendTemplateResponse>(
        `${this.notificationsUrl()}/send-template`,
        payload,
      )
      .pipe(catchError(() => of(null)));
  }

  listDispatches(
    filters: DispatchHistoryFilters = {},
  ): Observable<DispatchHistoryPage> {
    let params = new HttpParams();
    if (filters.dateFrom) params = params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params = params.set("dateTo", filters.dateTo);
    if (filters.page != null) params = params.set("page", filters.page);
    if (filters.size != null) params = params.set("size", filters.size);
    return this.http
      .get<DispatchHistoryPage>(`${this.notificationsUrl()}/history`, {
        params,
      })
      .pipe(
        tap(() => this.clearFailure("dispatches")),
        catchError((error) => {
          this.reportFailure("dispatches", error);
          return of({
            content: [],
            page: {
              number: 0,
              size: filters.size ?? 20,
              totalElements: 0,
              totalPages: 0,
            },
          });
        }),
      );
  }

  getDispatch(dispatchId: string): Observable<Dispatch | null> {
    return this.http
      .get<Dispatch>(`${this.notificationsUrl()}/history/${dispatchId}`)
      .pipe(
        tap(() => this.clearFailure("dispatches")),
        catchError((error) => {
          this.reportFailure("dispatches", error);
          return of(null);
        }),
      );
  }

  getDispatchRecipients(
    dispatchId: string,
    page = 0,
    size = 20,
  ): Observable<DispatchRecipientPage | null> {
    const params = new HttpParams().set("page", page).set("size", size);
    return this.http
      .get<DispatchRecipientPage>(
        `${this.notificationsUrl()}/history/${dispatchId}/recipients`,
        { params },
      )
      .pipe(
        tap(() => this.clearFailure("dispatches")),
        catchError((error) => {
          this.reportFailure("dispatches", error);
          return of(null);
        }),
      );
  }

  exportDispatches(
    filters: DispatchHistoryFilters = {},
  ): Observable<Blob | null> {
    let params = new HttpParams();
    if (filters.dateFrom) params = params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params = params.set("dateTo", filters.dateTo);
    return this.http
      .get(`${this.notificationsUrl()}/history/export`, {
        params,
        responseType: "blob",
      })
      .pipe(
        tap(() => this.clearFailure("dispatches")),
        catchError((error) => {
          this.reportFailure("dispatches", error);
          return of(null);
        }),
      );
  }

  markAsRead(notificationId: string): Observable<FeedItem | null> {
    const current = this.notificationsSubject.value.find(
      (item) => item.id === notificationId,
    );
    if (!current || current.userId !== this.currentUserId()) return of(null);

    if (current.isRead || this.pendingReadSnapshots.has(notificationId)) {
      return of(current);
    }

    this.pendingReadSnapshots.set(notificationId, current);
    this.updateUnreadCount(-1);
    this.upsertNotification({
      ...current,
      isRead: true,
      readAt: new Date().toISOString(),
    });

    return this.http
      .patch<FeedItem>(`${this.notificationsUrl()}/${notificationId}/read`, {})
      .pipe(
        tap((updated) => {
          this.pendingReadSnapshots.delete(notificationId);
          this.upsertNotification(updated);
        }),
        catchError(() => {
          const snapshot = this.pendingReadSnapshots.get(notificationId);
          this.pendingReadSnapshots.delete(notificationId);
          if (snapshot) {
            this.upsertNotification(snapshot);
            this.updateUnreadCount(1);
          }
          return of(null);
        }),
      );
  }

  downloadNotificationAttachment(
    notificationId: string,
    attachmentIndex: number,
  ): Observable<Blob | null> {
    const userId = this.currentUserId();
    if (!userId || attachmentIndex < 0) return of(null);

    return this.http
      .get(
        `${this.notificationsUrl()}/${notificationId}/attachments/${attachmentIndex}/download`,
        { responseType: "blob" },
      )
      .pipe(catchError(() => of(null)));
  }

  downloadDispatchAttachment(
    dispatchId: string,
    attachmentIndex: number,
  ): Observable<Blob | null> {
    if (attachmentIndex < 0) return of(null);

    return this.http
      .get(
        `${this.notificationsUrl()}/history/${dispatchId}/attachments/${attachmentIndex}/download`,
        { responseType: "blob" },
      )
      .pipe(catchError(() => of(null)));
  }

  deleteNotification(notificationId: string): Observable<boolean> {
    const userId = this.currentUserId();
    const current = this.notificationsSubject.value.find(
      (item) => item.id === notificationId,
    );
    if (!userId || !current || current.userId !== userId) return of(false);

    return this.http
      .delete<void>(`${this.notificationsUrl()}/${notificationId}`)
      .pipe(
        tap(() => this.removeNotification(notificationId, true)),
        map(() => true),
        catchError(() => of(false)),
      );
  }

  markAllAsRead(): Observable<UnreadSummary | null> {
    const userId = this.currentUserId();
    if (!userId) return of(null);

    return this.http
      .patch<UnreadSummary>(`${this.notificationsUrl()}/read-all`, {})
      .pipe(
        tap(() => {
          this.pendingReadSnapshots.clear();
          const updated = this.notificationsSubject.value.map((item) => ({
            ...item,
            isRead: true,
            readAt: item.readAt ?? new Date().toISOString(),
          }));
          this.setNotifications(updated);
          this.totalUnreadCountSubject.next(0);
        }),
        catchError(() => of(null)),
      );
  }

  private openStream(userId: number): void {
    const userKey = String(userId);
    if (this.connectedUserId === userKey && this.streamAbortController) return;
    if (typeof fetch === "undefined" || typeof ReadableStream === "undefined")
      return;

    this.disconnectStream();
    this.connectedUserId = userKey;
    this.startFetchStream(userKey);
  }

  private startFetchStream(userKey: string): void {
    if (this.connectedUserId !== userKey) return;

    const url = `${this.notificationsUrl()}${this.streamPath()}`;
    const controller = new AbortController();
    this.streamAbortController = controller;
    let accessToken: string;

    try {
      accessToken = this.sessionCtl.getAccessTokenOrThrow();
    } catch {
      this.releaseStreamController(controller);
      this.scheduleReconnect(userKey);
      return;
    }

    fetch(url, {
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(
            `Notification stream failed with status ${response.status}`,
          ) as Error & {
            status: number;
          };
          error.status = response.status;
          throw error;
        }
        if (!response.body)
          throw new Error("Notification stream response body is empty");
        this.zone.run(() => this.clearFailure("stream"));
        this.streamReconnectAttempt = 0;
        return this.readStream(response.body, controller.signal);
      })
      .then(() => {
        this.releaseStreamController(controller);
        if (!controller.signal.aborted && this.connectedUserId === userKey) {
          this.scheduleReconnect(userKey);
        }
      })
      .catch((error) => {
        this.releaseStreamController(controller);
        if (!controller.signal.aborted && this.connectedUserId === userKey) {
          this.zone.run(() => this.reportFailure("stream", error));
          this.scheduleReconnect(userKey);
        }
      });
  }

  private disconnectStream(): void {
    this.stopStream();
    this.connectedUserId = undefined;
    this.streamReconnectAttempt = 0;
  }

  private stopStream(): void {
    if (this.streamReconnectTimer) {
      clearTimeout(this.streamReconnectTimer);
      this.streamReconnectTimer = undefined;
    }
    this.streamAbortController?.abort();
    this.streamAbortController = undefined;
  }

  private releaseStreamController(controller: AbortController): void {
    if (this.streamAbortController === controller) {
      this.streamAbortController = undefined;
    }
  }

  private async readStream(
    stream: ReadableStream<Uint8Array>,
    signal: AbortSignal,
  ): Promise<void> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() ?? "";
        events.forEach((event) => this.handleStreamEvent(event));
      }
    } finally {
      reader.releaseLock();
    }
  }

  private handleStreamEvent(rawEvent: string): void {
    const lines = rawEvent.split(/\r?\n/);
    const eventName = lines
      .find((line) => line.startsWith("event:"))
      ?.slice(6)
      .trim();
    const data = lines
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart())
      .join("\n");

    if (eventName === "popup:refresh") {
      this.zone.run(() => this.popupRefreshSubject.next());
      return;
    }

    if (!data) return;

    this.zone.run(() => {
      let payload: unknown;

      try {
        payload = JSON.parse(data) as unknown;
      } catch {
        return;
      }

      if (eventName === "dispatch:read") {
        this.dispatchReadSubject.next(payload as DispatchReadEvent);
        return;
      }

      if (eventName === "dispatch:created") {
        this.dispatchCreatedSubject.next(payload as DispatchSummary);
        return;
      }

      if (eventName === "popup:interaction") {
        this.popupInteractionSubject.next(payload as InteractionEvent);
        return;
      }

      if (eventName === "notification:new") {
        this.receiveNotification(payload as FeedItem);
        return;
      }
      if (!eventName || eventName === "notification:read") {
        this.updateNotification(payload as FeedItem);
        return;
      }
      if (eventName === "notification:deleted") {
        const deleted = payload as NotificationDeletedEvent;
        if (deleted.userId === this.currentUserId())
          this.removeNotification(deleted.id, true);
        return;
      }
      if (eventName === "notifications:read-all") {
        this.applyReadAll(payload as StateEvent);
      }
    });
  }

  private applyReadAll(state: StateEvent): void {
    if (state.userId !== this.currentUserId()) return;

    const updated = this.notificationsSubject.value.map((item) => ({
      ...item,
      isRead: true,
      readAt: state.readAt ?? item.readAt ?? new Date().toISOString(),
    }));
    this.pendingReadSnapshots.clear();
    this.setNotifications(updated);
    this.totalUnreadCountSubject.next(0);
  }

  private scheduleReconnect(userKey: string): void {
    if (this.streamReconnectTimer) return;

    const exponentialDelay = Math.min(
      STREAM_RECONNECT_BASE_DELAY_MS * 2 ** this.streamReconnectAttempt,
      STREAM_RECONNECT_MAX_DELAY_MS,
    );
    const delay = exponentialDelay + Math.random() * STREAM_RECONNECT_JITTER_MS;
    this.streamReconnectAttempt += 1;

    this.streamReconnectTimer = setTimeout(() => {
      this.streamReconnectTimer = undefined;
      if (this.connectedUserId === userKey) {
        this.startFetchStream(userKey);
      }
    }, delay);
  }

  private upsertNotification(notification: FeedItem): void {
    const existing = this.notificationsSubject.value.filter(
      (item) => item.id !== notification.id,
    );
    const updated = [notification, ...existing];
    this.setNotifications(
      this.feedPageSize > 0 ? updated.slice(0, this.feedPageSize) : updated,
    );
  }

  private receiveNotification(notification: FeedItem): void {
    const exists = this.notificationsSubject.value.some(
      (item) => item.id === notification.id,
    );
    this.upsertNotification(notification);
    if (!exists && !notification.isRead) this.updateUnreadCount(1);
    this.newNotificationIndicatorSubject.next(true);
  }

  private updateNotification(notification: FeedItem): void {
    const current = this.notificationsSubject.value.find(
      (item) => item.id === notification.id,
    );
    this.upsertNotification(notification);
    if (current && !current.isRead && notification.isRead)
      this.updateUnreadCount(-1);
  }

  private removeNotification(
    notificationId: string,
    updateCount: boolean,
  ): void {
    const current = this.notificationsSubject.value.find(
      (item) => item.id === notificationId,
    );
    this.pendingReadSnapshots.delete(notificationId);
    this.setNotifications(
      this.notificationsSubject.value.filter(
        (item) => item.id !== notificationId,
      ),
    );
    if (updateCount && current && !current.isRead) this.updateUnreadCount(-1);
  }

  private setNotifications(items: FeedItem[]): void {
    const resolved = items.map((item) =>
      this.pendingReadSnapshots.has(item.id)
        ? {
            ...item,
            isRead: true,
            readAt: item.readAt ?? new Date().toISOString(),
          }
        : item,
    );
    const sorted = resolved.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    this.notificationsSubject.next(sorted);
  }

  private loadUnreadCount(userId: number): void {
    if (this.unreadCountUserId === userId || this.loadingUnreadCount) return;

    const requestId = ++this.unreadCountLoadSequence;
    this.loadingUnreadCount = true;
    this.http
      .get<UnreadSummary>(`${this.notificationsUrl()}/unread-count`)
      .pipe(
        tap((response) => {
          if (
            requestId !== this.unreadCountLoadSequence ||
            userId !== this.currentUserId()
          )
            return;
          this.unreadCountUserId = userId;
          this.totalUnreadCountSubject.next(Math.max(0, response.count));
        }),
        catchError(() => of(null)),
        finalize(() => {
          if (requestId === this.unreadCountLoadSequence)
            this.loadingUnreadCount = false;
        }),
      )
      .subscribe();
  }

  private updateUnreadCount(change: number): void {
    const count = Math.max(0, this.totalUnreadCountSubject.value + change);
    this.totalUnreadCountSubject.next(count);
  }

  private reset(): void {
    this.loadSequence += 1;
    this.disconnectStream();
    this.feedUserId = undefined;
    this.feedPageSize = 0;
    this.unreadCountUserId = undefined;
    this.loadingUnreadCount = false;
    this.unreadCountLoadSequence += 1;
    this.pendingReadSnapshots.clear();
    this.activeFailures.clear();
    this.publishedFailureKey = "";
    this.failureSubject.next(null);
    this.loadingSubject.next(false);
    this.notificationPageSubject.next({
      number: 0,
      size: 10,
      totalElements: 0,
      totalPages: 0,
    });
    this.setNotifications([]);
    this.totalUnreadCountSubject.next(0);
    this.newNotificationIndicatorSubject.next(false);
  }

  private isCurrentLoad(requestId: number, userId: number): boolean {
    return requestId === this.loadSequence && userId === this.currentUserId();
  }

  private emptyNotificationPage(page: number, size: number): NotificationPage {
    return {
      content: [],
      page: { number: page, size, totalElements: 0, totalPages: 0 },
    };
  }

  private mergeNotifications(
    current: FeedItem[],
    incoming: FeedItem[],
  ): FeedItem[] {
    const items = new Map(current.map((item) => [item.id, item]));
    incoming.forEach((item) => items.set(item.id, item));
    return Array.from(items.values());
  }

  private reportFailure(
    source: NotificationFailureSource,
    error: unknown,
  ): void {
    const failure = this.normalizeFailure(source, error);
    this.activeFailures.set(source, failure);
    this.publishFailure(failure);
  }

  private clearFailure(source: NotificationFailureSource): void {
    if (!this.activeFailures.delete(source)) return;

    const failures = Array.from(this.activeFailures.values());
    const remainingFailure = failures[failures.length - 1] ?? null;
    this.publishFailure(remainingFailure);
  }

  private publishFailure(failure: NotificationServiceFailure | null): void {
    const key = failure
      ? `${failure.source}:${failure.status}:${failure.code}`
      : "";
    if (key === this.publishedFailureKey) return;

    this.publishedFailureKey = key;
    this.failureSubject.next(failure);
  }

  private normalizeFailure(
    source: NotificationFailureSource,
    error: unknown,
  ): NotificationServiceFailure {
    const response = error as {
      status?: number;
      message?: string;
      error?: {
        code?: string;
        message?: string;
        description?: string;
        error?: string;
      };
    };
    const status = Number(response?.status) || 0;
    const code = String(
      response?.error?.code ?? (status ? `HTTP${status}` : "NETWORK_ERROR"),
    );
    const message = String(
      response?.error?.message ??
        response?.error?.description ??
        response?.error?.error ??
        "",
    ).trim();

    return { source, status, code, message };
  }

  private currentUserId(): number | null {
    const userId = this.sessionCtl.getUserId();
    return userId ?? null;
  }

  private notificationsUrl(): string {
    const env = this.resolveEnv();
    return `${env.host}${env.apiBasePath}`.replace(/\/$/, "");
  }

  private streamPath(): string {
    return this.resolveEnv().streamPath ?? "/stream";
  }

  private resolveEnv(): EnvMicroNotifications {
    return (
      this.notificationsEnv ??
      this.env.micros.notifications ?? {
        host: "http://localhost:8096",
        apiBasePath: "/notifications",
        streamPath: "/stream",
      }
    );
  }
}
